import { ApiRequestError } from '../services/api/client';

/**
 * Server code returned (with HTTP 409) when the accepted Terms document version
 * is no longer current. The visitor must re-read the updated Terms and accept
 * them again; the client never accepts the new version on their behalf.
 */
export const TERMS_VERSION_STALE_CODE = 'terms_version_stale';

const carriesStaleCode = (value: unknown): boolean =>
  typeof value === 'string' && value === TERMS_VERSION_STALE_CODE;

/**
 * True when an API rejection means the accepted Terms version is stale.
 *
 * The backend signals this with HTTP 409 and the `terms_version_stale` code.
 * The code can arrive as the error message or as an entry in the errors array;
 * a transport that exposes the HTTP status lets us match on 409 directly. All
 * three signals are checked so detection does not depend on one body shape.
 */
export const isTermsVersionStale = (error: unknown): boolean => {
  if (typeof error !== 'object' || error === null) {
    return false;
  }

  if ((error as { status?: number }).status === 409) {
    return true;
  }

  if (error instanceof ApiRequestError) {
    if (carriesStaleCode(error.message)) {
      return true;
    }

    return error.errors?.some(carriesStaleCode) ?? false;
  }

  return carriesStaleCode((error as { message?: unknown }).message);
};
